// Map.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import MapControls from "./Mapcontrols";
import AtonMarker from "./Atonmarker";
import RightPanelHeader from "./Rightpanelheader";
import RightPanelBody from "./Rightpanelbody";
import CoordinateHUD from "./Coordinatehud";
import EllipseOverlay from "./Ellipseoverlay";
import DohcOverlay from "./Dohcoverlay";
import {
  MapContainer,
  TileLayer,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import SmartFitBounds from "./Smartfitbounds";

const API_URL = "https://geoapi2s.s100lab.com";

const API_ATONS_LIST_URL = API_URL + "/api/geo/atons?page=1&size=10000";
const API_TURN_URL = API_URL + "/api/geo/aton";
const API_TURN_POS_URL = API_URL + "/api/geo/aton/pos";
const API_BRIDGE_LIST_URL = API_URL + "/api/geo/bridges";
const API_CABLE_LIST_URL = API_URL + "/api/geo/cables";
const API_BRIDGE_URL = API_URL + "/api/geo/bridges";
const API_CABLE_URL = API_URL + "/api/geo/cables";

/** ───────────── 좌표 파서(DMS/Decimal 지원) ───────────── */
const parseDMS = (str) => {
  if (str == null) return null;
  const s = String(str).trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  const m = s.match(/(\d+)[^\d]+(\d+)?[^\d]+(\d+(?:\.\d+)?)?\s*([NSEW])?/i);
  if (!m) return null;
  const deg = parseFloat(m[1] || "0");
  const min = parseFloat(m[2] || "0");
  const sec = parseFloat(m[3] || "0");
  let val = deg + min / 60 + sec / 3600;
  const hemi = (m[4] || "").toUpperCase();
  if (hemi === "S" || hemi === "W") val = -val;
  return val;
};

const InvalidateSize = ({ deps }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 220);
    return () => clearTimeout(t);
  }, [deps, map]);
  return null;
};

// ---------- 응답 정규화 유틸(선회반경) ----------
const toNum = (v) => (v == null ? NaN : Number(v));

function normalizeTurnItem(raw, idx = 0) {
  if (!raw || typeof raw !== "object") return null;
  const lat = toNum(raw.latitude_dd ?? raw.center_lat ?? raw.lat ?? raw.latitude);
  const lng = toNum(raw.longitude_dd ?? raw.center_lng ?? raw.lon ?? raw.lng ?? raw.longitude);
  const radius = toNum(raw.radius_m ?? raw.radiusMeters ?? raw.radius);
  const pts = raw.circle_points_wgs84 ?? raw.ellipse ?? raw.polygon ?? raw.points ?? [];
  return {
    id: String(raw.id ?? idx),
    circle_points_wgs84: pts,
    radius_m: Number.isFinite(radius) ? radius : undefined,
    latitude_dd: Number.isFinite(lat) ? lat : undefined,
    longitude_dd: Number.isFinite(lng) ? lng : undefined,
  };
}

function normalizeTurnResponse(json) {
  const root = Array.isArray(json) ? json
    : Array.isArray(json?.data) ? json.data
      : Array.isArray(json?.result) ? json.result
        : json?.items && Array.isArray(json.items) ? json.items
          : json ? [json] : [];
  const out = [];
  root.forEach((it, i) => {
    const n = normalizeTurnItem(it, i);
    if (n) out.push(n);
  });
  return out;
}

/** lat_dd{n}, lng_dd{n} 시퀀스로 폴리곤 좌표 만들기 */
function buildPolygonFromLatLngQuads(raw, maxN = 20) {
  const pts = [];
  for (let i = 1; i <= maxN; i++) {
    const lat = toNum(raw?.[`lat_dd${i}`]);
    const lng = toNum(raw?.[`lng_dd${i}`]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      pts.push([lat, lng]);
    } else {
      if (raw?.[`lat_dd${i}`] == null && raw?.[`lng_dd${i}`] == null) break;
    }
  }
  if (pts.length >= 3) {
    const [fl, fg] = pts[0],
      [ll, lg] = pts[pts.length - 1];
    if (fl !== ll || fg !== lg) pts.push([fl, fg]);
  }

  return pts;
}

/** ── DOHC 응답 정규화 (교량/케이블 공통) ── */
function normalizeDohcItem(raw, idx = 0) {
  if (!raw || typeof raw !== "object") return null;

  let polygon = buildPolygonFromLatLngQuads(raw);
  // if (polygon.length < 3) {
  //   polygon =
  //     raw?.polygon ??
  //     raw?.area ??
  //     raw?.geom ??
  //     raw?.geometry ??
  //     raw?.shape ??
  //     null;
  // }

  const id = String(raw?.id ?? raw?.bridge_id ?? raw?.cable_id ?? idx);
  const name_ko = raw?.name_ko ?? "";
  const name_en = raw?.name_en ?? "";
  const sub_name_ko = raw?.sub_name_ko ?? "";
  const sub_name_en = raw?.sub_name_en ?? "";

  const structure_proximity_radius = toNum(raw?.structure_proximity_radius);
  const air_draft = toNum(raw?.air_draft);
  const clear = toNum(raw?.clear);
  const caution = toNum(raw?.caution);
  const no_access = toNum(raw?.no_access);
  const level_m = toNum(raw?.level_m);
  const z0_m = toNum(raw?.z0_m);
  const structure_height = toNum(raw?.structure_height);
  const water_level_height = toNum(raw?.water_level_height);
  const air_gap = toNum(raw?.air_gap);
  const dohc = toNum(raw?.dohc);
  const dohc_status = raw?.dohc_status || "Clear";
  const passage_time = raw?.passage_time;
  const latitude1 = raw?.latitude1;
  const longitude1 = raw?.longitude1;
  const latitude2 = raw?.latitude2;
  const longitude2 = raw?.longitude2;
  const latitude3 = raw?.latitude3;
  const longitude3 = raw?.longitude3;
  const latitude4 = raw?.latitude4;
  const longitude4 = raw?.longitude4;
  const lat_dd1 = toNum(raw?.lat_dd1);
  const lng_dd1 = toNum(raw?.lng_dd1);
  const lat_dd2 = toNum(raw?.lat_dd2);
  const lng_dd2 = toNum(raw?.lng_dd2);
  const lat_dd3 = toNum(raw?.lat_dd3);
  const lng_dd3 = toNum(raw?.lng_dd3);
  const lat_dd4 = toNum(raw?.lat_dd4);
  const lng_dd4 = toNum(raw?.lng_dd4);

  const circle_points_wgs84 = raw?.circle_points_wgs84 ?? null;

  return {
    id,
    name_ko,
    name_en,
    sub_name_ko,
    sub_name_en,
    facility_type: raw?.facility_type || "",
    polygon,
    dohc_status,
    circle_points_wgs84,
    structure_proximity_radius,
    passage_time,
    air_draft,
    clear,
    caution,
    no_access,
    level_m,
    z0_m,
    structure_height,
    water_level_height,
    air_gap,
    dohc,
    latitude1, longitude1,
    latitude2, longitude2,
    latitude3, longitude3,
    latitude4, longitude4,
    lat_dd1, lng_dd1,
    lat_dd2, lng_dd2,
    lat_dd3, lng_dd3,
    lat_dd4, lng_dd4
  };
}

function normalizeDohcResponse(json) {
  const arr = Array.isArray(json)
    ? json
    : Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json?.result)
        ? json.result
        : json?.items && Array.isArray(json.items)
          ? json.items
          : json
            ? [json]
            : [];
  return arr.map((it, i) => normalizeDohcItem(it, i)).filter(Boolean);
}

export const Map = (props) => {
  const [map, setMap] = useState(null);
  const [atons, setAtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // 선회반경(선택 가시화) 결과
  const [turnResult, setTurnResult] = useState([]);
  const [turnLoading, setTurnLoading] = useState(false);
  const [turnErr, setTurnErr] = useState(null);

  // 좌표+반경 검색 결과(패널 테이블)
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchErr, setSearchErr] = useState(null);

  // ✅ 교량/케이블 목록 & 상태
  const [bridges, setBridges] = useState([]);
  const [bridgesLoading, setBridgesLoading] = useState(false);
  const [bridgesErr, setBridgesErr] = useState(null);

  const [cables, setCables] = useState([]);
  const [cablesLoading, setCablesLoading] = useState(false);
  const [cablesErr, setCablesErr] = useState(null);

  // DOHC 가시화 결과
  const [bridgeDohcResults, setBridgeDohcResults] = useState([]);
  const [bridgeDohcLoading, setBridgeDohcLoading] = useState(false);
  const [bridgeDohcErr, setBridgeDohcErr] = useState(null);

  const [cableDohcResults, setCableDohcResults] = useState([]);
  const [cableDohcLoading, setCableDohcLoading] = useState(false);
  const [cableDohcErr, setCableDohcErr] = useState(null);

  const fsRef = useRef(null);

  // 목록 fetch (AtoN)
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await fetch(API_ATONS_LIST_URL, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.data ?? []);
        const parsed = list
          .map((it) => {
            const lat = parseDMS(it.latitude);
            const lng = parseDMS(it.longitude);
            return {
              id: it.id ?? "",
              name_en: it.name_en ?? "",
              name_ko: it.name_ko ?? "",
              symbol: "/img/svg/" + it.symbol + ".svg",
              aton_number: it.aton_number ?? "",
              color: it.color ?? "",
              shape: it.shape ?? "",
              chain_length: it.chain_length ?? "",
              mooring_chain_length: it.mooring_chain_length ?? "",
              transition: it.transition ?? "",
              connection: it.connection ?? "",
              sinker: it.sinker ?? "",
              total_chain_length: it.total_chain_length ?? "",
              sounding: it.sounding ?? "",
              level_m: it.level_m ?? "",
              last_buoy_work_date: it.last_buoy_work_date ?? "",
              registration_date: it.registration_date ?? "",
              latitude: it.latitude ?? "",
              longitude: it.longitude ?? "",
              lat, lng,
            };
          })
          .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
        setAtons(parsed);
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // 교량 목록 fetch
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setBridgesLoading(true);
        setBridgesErr(null);
        const res = await fetch(API_BRIDGE_LIST_URL, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.data ?? []);
        const parsed = list.map((b, i) => ({
          id: String(b.id ?? i),
          name: b.name_ko || b.name_en || b.name || `Bridge ${i + 1}`,
          ...b,
        }));
        setBridges(parsed);
      } catch (e) {
        if (e.name !== "AbortError") setBridgesErr(e.message || String(e));
      } finally {
        setBridgesLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // 케이블 목록 fetch
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setCablesLoading(true);
        setCablesErr(null);
        const res = await fetch(API_CABLE_LIST_URL, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.data ?? []);
        const parsed = list.map((c, i) => ({
          id: String(c.id ?? i),
          name: c.name_ko || c.name_en || c.name || `Cable ${i + 1}`,
          ...c,
        }));
        setCables(parsed);
      } catch (e) {
        if (e.name !== "AbortError") setCablesErr(e.message || String(e));
      } finally {
        setCablesLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // API #1: 선택 가시화(타원)
  const onRunTurn = useCallback(async ({ ids, radius }) => {
    try {
      setTurnLoading(true);
      setTurnErr(null);
      const res = await fetch(API_TURN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, radius }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const arr = normalizeTurnResponse(json);
      setTurnResult(arr);
    } catch (e) {
      setTurnErr(e.message || String(e));
      setTurnResult([]);
    } finally {
      setTurnLoading(false);
    }
  }, []);

  // API #2: 좌표+반경 검색(목록)
  const onSearchAtonsByCircle = useCallback(async ({ latitude, longitude, radius }) => {
    try {
      setSearchLoading(true);
      setSearchErr(null);
      const payload = {
        latitude: Number(latitude),
        longitude: Number(longitude),
        radius: Number(radius),
      };
      const res = await fetch(API_TURN_POS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : json?.data ?? [];
      const parsed = list
        .map((it, i) => ({
          id: String(it.id ?? i),
          name: it.name_ko || it.name_en || "표지",
          lat: Number(it.latitude_dd),
          lng: Number(it.longitude_dd),
          radius_m: Number(it.radius_m),
          distance_m: Number(it.distance_m ?? NaN),
          circle_points_wgs84: it.circle_points_wgs84,
        }))
        .filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lng));
      setSearchResults(parsed);
    } catch (e) {
      setSearchErr(e.message || String(e));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // ── 교량 DOHC 실행 ───────────────────────────────────────────────────
  const onRunBridgeDohc = useCallback(
    async ({ ids = [], structure_proximity_radius, air_draft, passage_time, clear, caution, no_access }) => {
      const ctrl = new AbortController();
      try {
        setBridgeDohcLoading(true);
        setBridgeDohcErr(null);

        const payload = {
          ids,
          facility_type: "Bridge",
          structure_proximity_radius,
          air_draft,
          passage_time,
          clear,
          caution,
          no_access,
        };

        const res = await fetch(API_BRIDGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const norm = normalizeDohcResponse(json);
        setBridgeDohcResults(norm);
      } catch (e) {
        setBridgeDohcErr(e.message || String(e));
        setBridgeDohcResults([]);
      } finally {
        setBridgeDohcLoading(false);
      }
      return () => ctrl.abort();
    },
    []
  );

  // ── 케이블 DOHC 실행 ─────────────────────────────────────────────────
  const onRunCableDohc = useCallback(
    async ({ ids = [], structure_proximity_radius, air_draft, passage_time, clear, caution, no_access }) => {
      const ctrl = new AbortController();
      try {
        setCableDohcLoading(true);
        setCableDohcErr(null);

        const payload = {
          ids,
          facility_type: "Cable",
          structure_proximity_radius,
          air_draft,
          passage_time,
          clear,
          caution,
          no_access,
        };

        const res = await fetch(API_CABLE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const norm = normalizeDohcResponse(json);
        setCableDohcResults(norm);
      } catch (e) {
        setCableDohcErr(e.message || String(e));
        setCableDohcResults([]);
      } finally {
        setCableDohcLoading(false);
      }
      return () => ctrl.abort();
    },
    []
  );

  const clearBridgeDohc = useCallback(() => {
    setBridgeDohcResults([]);
    setBridgeDohcErr(null);
  }, []);

  const clearCableDohc = useCallback(() => {
    setCableDohcResults([]);
    setCableDohcErr(null);
  }, []);

  const clearRunTurn = useCallback(() => {
    setTurnResult([]);
    setTurnErr(null);
  }, []);

  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setSearchErr(null);
  }, []);

  // fitBounds 대상 (선회반경 + DOHC 폴리곤까지 반영)
  const allPositions = useMemo(() => {
    const ellipsePts = [];
    for (const item of turnResult || []) {
      const pts = item?.circle_points_wgs84 ?? [];
      for (const p of pts) {
        const lat = Array.isArray(p) ? Number(p[0]) : Number(p?.lat);
        const lng = Array.isArray(p) ? Number(p[1]) : Number(p?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) ellipsePts.push([lat, lng]);
      }
    }

    const dohcPts = [];
    const collect = (geom) => {
      if (!geom) return;
      if (geom.type === "Polygon" && Array.isArray(geom.coordinates?.[0])) {
        for (const [lng, lat] of geom.coordinates[0]) {
          if (Number.isFinite(lat) && Number.isFinite(lng)) dohcPts.push([lat, lng]);
        }
      } else if (Array.isArray(geom) && geom.length && Array.isArray(geom[0])) {
        for (const p of geom) {
          const lat = Number(p[0]);
          const lng = Number(p[1]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) dohcPts.push([lat, lng]);
        }
      }
    };
    (bridgeDohcResults || []).forEach((it) => collect(it.polygon));
    (cableDohcResults || []).forEach((it) => collect(it.polygon));

    return [
      ...atons.map((a) => [a.lat, a.lng]),
      ...ellipsePts,
      ...dohcPts,
    ];
  }, [atons, turnResult, bridgeDohcResults, cableDohcResults]);

  const handleRowDoubleClick = useCallback(
    (a) => {
      if (!map) return;
      if (!Number.isFinite(a?.lat) || !Number.isFinite(a?.lng)) return;
      const targetZoom = Math.max(map.getZoom() || 0, 16);
      try {
        map.flyTo([a.lat, a.lng], targetZoom, { duration: 0.8 });
      } catch {
        map.setView([a.lat, a.lng], targetZoom);
      }
    },
    [map]
  );

  const MapReady = ({ onReady }) => {
    const m = useMap();
    useEffect(() => {
      if (m) onReady?.(m);
    }, [m, onReady]);
    return null;
  };

  return (
    <div id="map" className="text-center">
      <div className="container">
        <div className="section-title">
          <h2>데이터 가시화</h2>
          <p>지도 기반의 데이터 (DOHC, 선회반경) 가시화를 제공합니다.</p>
        </div>

        {loading && <div style={{ marginBottom: 8 }}>불러오는 중…</div>}
        {err && <div style={{ color: "crimson", marginBottom: 8 }}>에러: {err}</div>}
        {turnLoading && <div style={{ color: "#2563eb", marginBottom: 8 }}>선회반경 계산 중…</div>}
        {turnErr && <div style={{ color: "crimson", marginBottom: 8 }}>선회반경 에러: {turnErr}</div>}
        {bridgeDohcLoading && <div style={{ color: "#2563eb", marginBottom: 8 }}>교량 DOHC 가시화 중…</div>}
        {bridgeDohcErr && <div style={{ color: "crimson", marginBottom: 8 }}>교량 DOHC 에러: {bridgeDohcErr}</div>}
        {cableDohcLoading && <div style={{ color: "#2563eb", marginBottom: 8 }}>케이블 DOHC 가시화 중…</div>}
        {cableDohcErr && <div style={{ color: "crimson", marginBottom: 8 }}>케이블 DOHC 에러: {cableDohcErr}</div>}

        <div
          ref={fsRef}
          className="relative w-full"
          style={{ height: 800, display: "flex", overflow: "hidden" }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <MapContainer
              center={[34.790432, 126.357516]}
              zoom={18}
              scrollWheelZoom={true}
              zoomControl={false}
              style={{ height: "100%", width: "100%" }}
            >
              <MapReady onReady={setMap} />

              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapControls
                zoomStep={1}
                fullscreenTargetRef={fsRef}
                panelOpen={panelOpen}
                onTogglePanel={() => setPanelOpen((v) => !v)}
                onClosePanel={() => setPanelOpen(false)}
              />

              {/* AtoN 마커들 */}
              {atons.map((a) => (
                <AtonMarker key={a.id} a={a} />
              ))}

              {/* 선회반경(타원) 오버레이 */}
              {Array.isArray(turnResult) &&
                turnResult.map((item, idx) => (
                  <EllipseOverlay
                    key={item.id ?? `ellipse-${idx}`}
                    points={
                      item?.circle_points_wgs84?.type && item?.circle_points_wgs84?.coordinates
                        ? item.circle_points_wgs84
                        : item?.circle_points_wgs84 ?? []
                    }
                    strokeColor="#FFC000"
                    strokeWeight={2}
                    fillOpacity={0.25}
                    radius_m={item?.radius_m}
                    latitude_dd={item?.latitude_dd}
                    longitude_dd={item?.longitude_dd}
                    radiusBearingDeg={90}
                    showCenter
                    showRadius
                    radiusColor="#ff2d2d"
                  />
                ))}

              {/* 좌표+반경 검색 결과 타원 */}
              {Array.isArray(searchResults) &&
                searchResults.map((item, idx) => (
                  <EllipseOverlay
                    key={item.id ?? `ellipse-${idx}`}
                    points={
                      item?.circle_points_wgs84?.type && item?.circle_points_wgs84?.coordinates
                        ? item.circle_points_wgs84
                        : item?.circle_points_wgs84 ?? []
                    }
                    strokeColor="#2F8E20"
                    strokeWeight={2}
                    fillOpacity={0.25}
                    radius_m={item?.radius_m}
                    latitude_dd={item?.lat}
                    longitude_dd={item?.lng}
                    radiusBearingDeg={90}
                    showCenter
                    showRadius
                    radiusColor="#ff2d2d"
                  />
                ))}

              {/* 교량 DOHC 오버레이 */}
              {Array.isArray(bridgeDohcResults) &&
                bridgeDohcResults.map((item) => {
                  return (
                    <DohcOverlay
                      data={item}
                      key={`bridge-${item.id}`}
                      geometry={item.polygon}
                      dohc_status={item.dohc_status}
                      label={item.dohc}
                      circle_points_wgs84={item.circle_points_wgs84}
                      structure_proximity_radius={item.structure_proximity_radius}
                    />
                  );
                })}

              {/* 케이블 DOHC 오버레이 */}
              {Array.isArray(cableDohcResults) &&
                cableDohcResults.map((item) => {
                  return (
                    <DohcOverlay
                      data={item}
                      key={`cable-${item.id}`}
                      geometry={item.polygon}
                      dohc_status={item.dohc_status}
                      label={item.dohc}
                      circle_points_wgs84={item.circle_points_wgs84}
                      structure_proximity_radius={item.structure_proximity_radius}
                    />
                  );
                })}

              <CoordinateHUD position="bottomright" format="dec" digits={6} />

              {/* 초기 1회만 영역 맞춤 */}
              <SmartFitBounds
                points={allPositions}
                pad={0.1}
                enabled={true}
                firstOnly={true}
                coolDownMs={1500}
              />
              <InvalidateSize deps={panelOpen} />
            </MapContainer>
          </div>

          {/* 오른쪽 패널 */}
          <aside
            style={{
              width: panelOpen ? 500 : 0,
              transition: "width 200ms ease",
              overflow: "hidden",
              borderLeft: "1px solid #e5e7eb",
              background: "rgba(255,255,255,1)",
              backdropFilter: "blur(6px)",
            }}
          >
            <RightPanelHeader
              logoSrc="/img/geo/setting.png"
              logoAlt="GEO"
              brand="GEO"
              onClose={() => setPanelOpen(false)}
            />
            <div style={{ padding: 12 }}>
              <RightPanelBody
                defaultTab="turn"
                atons={atons}
                onRowDoubleClick={handleRowDoubleClick}
                // ⬇️ API #1 (선회반경 가시화)
                onRunTurn={onRunTurn}
                // ⬇️ API #2 (좌표+반경 검색)
                onSearchAtonsByCircle={onSearchAtonsByCircle}
                searchResults={searchResults}
                searchLoading={searchLoading}
                searchErr={searchErr}
                onClearSearchResults={clearSearchResults}
                onclearRunTurn={clearRunTurn}
                // ⬇️ 교량/케이블 목록 및 DOHC 실행/초기화
                bridges={bridges}
                bridgesLoading={bridgesLoading}
                bridgesErr={bridgesErr}
                cables={cables}
                cablesLoading={cablesLoading}
                cablesErr={cablesErr}
                onRunBridgeDohc={onRunBridgeDohc}
                onRunCableDohc={onRunCableDohc}
                onClearBridgeDohc={clearBridgeDohc}
                onClearCableDohc={clearCableDohc}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
